import type { Scene } from './core/scene/Scene';
import { BaseAsset } from './core/asset/BaseAsset';
import { TimeAwareBase } from './core/TimeAwareBase';
import { AssetGroup } from './core/asset/AssetGroup';
import { Config } from './video-data/config';
import type Luminary from './index';

// Examples

// /scenes
// /*/MediaAsset
// /scenes/**/MediaAsset
// /scenes/AssetGroup/MediaAsset
// /**/MediaAsset
// /**/MediaAsset/name = 'media_2'
// /**/MediaAsset/mask
// /**/MediaAsset/name != 'media_1' | $/isEditable == true | $/isEditable = false

type TokenType = string;

type Token = {
    type: TokenType;
    index: number;
    nextIndex: number;
    text: string;
};

type InterpreterContext = {
    query: String;
    tokens: Token[];
    index: number;
    stack: unknown[];
    path: Token[];
    scene: Scene;
};

type Matcher = {
    pattern: RegExp;
    tokenType: string;
};

const aliases: Record<string, string> = {
    TextAsset: Config.TEXT_ASSET_TYPE,
    MediaAsset: Config.MEDIA_ASSET_TYPE,
};

class NoMatch {
    context: InterpreterContext;
    token: Token;
    message: string;

    constructor(context: InterpreterContext, token: Token, message: string) {
        this.context = context;
        this.token = token;
        this.message = message;
    }

    get contextualMessage() {
        return `${this.message}\n${this.context.query}\n${'^'.padStart(this.token.index, ' ')}`;
    }

    logMessage() {
        console.log(this.contextualMessage);
    }
}

const help = `Find and edit assets in a Luminary instance.

# The Basics
Luminary assets form a tree. Think of it as a file directory.
/  is root
*  matches everything in the current directory
** matches everything recursively
|  pipe results into another expression
$  the item in the pipe

# Indexing
The query language is about extracting results from arrays of found assets. This makes an index context sensitive:
\`/**/MediaAsset/filters/0/name\`, if there are two media assets, this returns two results, the names of the first filter
                                  in each media asset.
\`/**/MediaAsset/0/filters/0/name\`, if there are two media assets, grab the first returning the name of the first filter
\`/**/MediaAsset/filters/*/name\`, returns the names of all filters in all media assets

# Examples
/*                                      - all assets on the scene level OR as a wildcard to get all array elements
/**                                     - all assets on the scene and subscene level
/**/MediaAsset                          - all media assets
/**/MediaAsset/name == 'media_2'        - all media assets named 'media_2'
/**/MediaAsset/name == 'media_2'        - all media assets named 'media_2'
/**/MediaAsset | $/name == 'media_2'    - same as above
/**/MediaAsset/name == 'media_2' | $/isEditable = false
                                        - set the asset to not editable`;

const question = /\?/y;
const whiteSpace = /\s+/y;
const pipe = /\|/y;
const eq = /==/y;
const neq = /!=/y;
const gte = />=/y;
const gt = />/y;
const lte = /<=/y;
const lt = /</y;
const assign = /=/y;

const stringSingleQuote = /'.*?'/y;
const stringDoubleQuote = /".*?"/y;
const btrue = /true/y;
const bfalse = /false/y;
const number = /\d+(\.\d+)?/y;

const propertyName = /[a-z][a-zA-Z0-9_]*/y;
const className = /[A-Z][a-zA-Z0-9]*/y;
const input = /\$/y;

const pathSeparator = /\//y;
const wildRecursive = /\*\*/y;
const wild = /\*/y;

// Token ids
const T_WHITE_SPACE = 'T_WHITE_SPACE';
const T_PIPE = 'T_PIPE';
const T_EQ = 'T_EQ';
const T_NEQ = 'T_NEQ';
const T_GTE = 'T_GTE';
const T_GT = 'T_GT';
const T_LTE = 'T_LTE';
const T_LT = 'T_LT';
const T_ASSIGN = 'T_ASSIGN';

const T_STRING = 'T_STRING';
const T_TRUE = 'T_TRUE';
const T_FALSE = 'T_FALSE';
const T_NUMBER = 'T_NUMBER';

const T_PROPERTY_NAME = 'T_PROPERTY_NAME';
const T_CLASS_NAME = 'T_CLASS_NAME';
const T_INPUT = 'T_INPUT';

const T_PATH_SEPARATOR = 'T_PATH_SEPARATOR';
const T_WILD_RECURSIVE = 'T_WILD_RECURSIVE';
const T_WILD = 'T_WILD';

const T_QUESTION = 'T_QUESTION';

// Special 'end of token "file"' token.
const T_EOF = 'T_EOF';

// In rough order of fastest match, most specific match (e.g. 'true' vs some identifier')  and most commonly used, and
// precedence (e.g ** must be before *).
const matchers: Matcher[] = [
    { pattern: whiteSpace, tokenType: T_WHITE_SPACE },
    { pattern: pipe, tokenType: T_PIPE },
    { pattern: pathSeparator, tokenType: T_PATH_SEPARATOR },
    { pattern: wildRecursive, tokenType: T_WILD_RECURSIVE },
    { pattern: wild, tokenType: T_WILD },
    { pattern: input, tokenType: T_INPUT },
    { pattern: className, tokenType: T_CLASS_NAME },
    { pattern: btrue, tokenType: T_TRUE },
    { pattern: bfalse, tokenType: T_FALSE },
    { pattern: propertyName, tokenType: T_PROPERTY_NAME },

    { pattern: eq, tokenType: T_EQ },
    { pattern: neq, tokenType: T_NEQ },
    { pattern: gte, tokenType: T_GTE },
    { pattern: gt, tokenType: T_GT },
    { pattern: lte, tokenType: T_LTE },
    { pattern: lt, tokenType: T_LT },
    { pattern: assign, tokenType: T_ASSIGN },

    { pattern: stringSingleQuote, tokenType: T_STRING },
    { pattern: stringDoubleQuote, tokenType: T_STRING },
    { pattern: number, tokenType: T_NUMBER },

    { pattern: question, tokenType: T_QUESTION },
];

function matchNextToken(source: string, fromIndex: number, matchers: Matcher[]): Token {
    if (source.length === fromIndex) {
        return null;
    }

    for (const matcher of matchers) {
        matcher.pattern.lastIndex = fromIndex;
        const result = matcher.pattern.exec(source);
        if (result) {
            let text = result[0];
            if (matcher.tokenType === T_CLASS_NAME) {
                text = aliases[text] ?? text;
            }
            return {
                type: matcher.tokenType,
                index: result.index,
                text,
                nextIndex: result.index + result[0].length,
            };
        }
    }

    throw new Error(`unknown token at ${fromIndex}`);
}

function tokenize(queryString: string): Token[] {
    const tokens: Token[] = [];

    let startIndex = 0;
    let result: Token = matchNextToken(queryString, startIndex, matchers);
    while (result) {
        // Ignore whitespace, it's not part of parsing. So '/    *' is a valid query.
        if (result.type !== T_WHITE_SPACE) {
            tokens.push(result);
        }
        startIndex = result.nextIndex;
        result = matchNextToken(queryString, startIndex, matchers);
    }

    return tokens;
}

/*
Test output is:

[
  { type: 'T_PATH_SEPARATOR', index: 0, text: '/', nextIndex: 1 },
  { type: 'T_WILD_RECURSIVE', index: 1, text: '**', nextIndex: 3 },
  { type: 'T_PATH_SEPARATOR', index: 3, text: '/', nextIndex: 4 },
  { type: 'T_CLASS_NAME', index: 4, text: 'MediaAsset', nextIndex: 14 },
  { type: 'T_PATH_SEPARATOR', index: 14, text: '/', nextIndex: 15 },
  { type: 'T_PROPERTY_NAME', index: 15, text: 'name', nextIndex: 19 },
  { type: 'T_NEQ', index: 21, text: '!=', nextIndex: 23 },
  { type: 'T_STRING', index: 24, text: "'media_1'", nextIndex: 33 },
  { type: 'T_PIPE', index: 34, text: '|', nextIndex: 35 },
  { type: 'T_INPUT', index: 36, text: '$', nextIndex: 37 },
  { type: 'T_PATH_SEPARATOR', index: 37, text: '/', nextIndex: 38 },
  {
    type: 'T_PROPERTY_NAME',
    index: 38,
    text: 'isEditable',
    nextIndex: 48
  },
  { type: 'T_EQ', index: 49, text: '==', nextIndex: 51 },
  { type: 'T_PROPERTY_NAME', index: 52, text: 'true', nextIndex: 56 },
  { type: 'T_PIPE', index: 57, text: '|', nextIndex: 58 },
  { type: 'T_INPUT', index: 59, text: '$', nextIndex: 60 },
  { type: 'T_PATH_SEPARATOR', index: 60, text: '/', nextIndex: 61 },
  {
    type: 'T_PROPERTY_NAME',
    index: 61,
    text: 'isEditable',
    nextIndex: 71
  },
  { type: 'T_ASSIGN', index: 72, text: '=', nextIndex: 73 },
  { type: 'T_PROPERTY_NAME', index: 74, text: 'false', nextIndex: 79 }
]

*/

function error(context: InterpreterContext, message: string) {
    throw new Error(message);
}

function expectToken(context: InterpreterContext, expectedType: TokenType, type: TokenType) {
    if (expectedType !== type) {
        throw new Error(`Expected token '${expectedType}' but found '${type}'`);
    }
}

function unexpectedTokenError(context: InterpreterContext, token: Token) {
    throw new Error(`Unexpected ${token.text}:${token.index}`);
}

// Token stream functions.

function eof(context: InterpreterContext): Token {
    return {
        type: T_EOF,
        index: context.index + 1,
        nextIndex: context.index + 1,
        text: '<EOF>',
    };
}

function nextToken(context: InterpreterContext): Token {
    if (context.index + 1 === context.tokens.length) {
        return eof(context);
    }

    context.index += 1;
    return context.tokens[context.index];
}

function peekToken(context: InterpreterContext): Token {
    const index = context.index + 1;
    return index < context.tokens.length ? context.tokens[index] : eof(context);
}

// Stack operations.

function push(context: InterpreterContext, value: unknown) {
    context.stack.push(value);
}

function pop(context: InterpreterContext): unknown {
    return context.stack.pop();
}

function stackTop(context: InterpreterContext): unknown {
    return context.stack[context.stack.length - 1];
}

// Path operations.

function pushPath(context: InterpreterContext, pathComponent: Token) {
    context.path.push(pathComponent);
}

function lastPathComponent(context: InterpreterContext) {
    return context.path[context.path.length - 1];
}

export function query(luminary: Luminary, queryString: string) {
    const tokens = tokenize(queryString);
    const results = [];

    if (tokens.length === 0) {
        return;
    }

    if (tokens[0].text === '?') {
        console.log(help);
        return;
    }

    for (const scene of luminary.scenes) {
        const context: InterpreterContext = {
            query: queryString,
            tokens,
            index: -1,
            stack: [],
            path: [],
            scene,
        };
        start(context);
        results.push(stackTop(context));
    }

    // TODO: need a better results, grouped by scene, subscene etc. result = { object, sceneIndex, sceneDesign } or
    // something.
    return results.flat();
}

// The interpreter
//
// start: expr
function start(context: InterpreterContext) {
    expr(context);
}

// expr: path (assignExpr | condExpr)? (pipe expr)?
function expr(context: InterpreterContext) {
    path(context);
    switch (peekToken(context).type) {
        case T_EQ:
        case T_NEQ:
        case T_LT:
        case T_LTE:
        case T_GT:
        case T_GTE:
            condExpr(context);
            break;
        case T_ASSIGN:
            assignExpr(context);
            break;
    }

    if (peekToken(context).type === T_PIPE) {
        nextToken(context); // Skip the pipe, it's just a marker to separate filtering sections.
        expr(context);
    }
}

// condExpr: op atom
function condExpr(context: InterpreterContext) {
    const operator = nextToken(context);
    atom(context);
    const operand = pop(context);

    // TODO: better error checking here, these only make sense when the path is a property.
    if (lastPathComponent(context).type !== T_PROPERTY_NAME) {
        throw new Error('Expecting a property name in order to use a conditional');
    }

    const property = lastPathComponent(context).text;
    // Pop the values of the properties that are on the top of the stack.
    pop(context);
    // Grab the actual assets on which to query the property.
    const nodes = stackTop(context) as Record<string, unknown>[];
    switch (operator.type) {
        case T_EQ:
            push(
                context,
                nodes.filter((n) => n[property] === operand),
            );
            break;
        case T_NEQ:
            push(
                context,
                nodes.filter((n) => n[property] !== operand),
            );
            break;
        case T_LT:
            push(
                context,
                nodes.filter((n) => n[property] < operand),
            );
            break;
        case T_LTE:
            push(
                context,
                nodes.filter((n) => n[property] <= operand),
            );
            break;
        case T_GT:
            push(
                context,
                nodes.filter((n) => n[property] > operand),
            );
            break;
        case T_GTE:
            push(
                context,
                nodes.filter((n) => n[property] >= operand),
            );
            break;
        default:
            error(context, `Unknown operator ${operator.text}`);
    }
}

function assignExpr(context: InterpreterContext) {
    nextToken(context); // Skip '='
    atom(context);
    const value = pop(context);
    pop(context); // Remove the propery values, we want the nodes.
    const nodes = stackTop(context) as Record<string, unknown>[];

    const prop = lastPathComponent(context);
    expectToken(context, prop.type, T_PROPERTY_NAME);
    nodes.forEach((n) => {
        n[prop.text] = value;
    });
}

// atom: NUMBER | TRUE | FALSE | STRING
function atom(context: InterpreterContext) {
    const token = nextToken(context);
    switch (token.type) {
        case T_NUMBER:
            push(context, Number(token.text));
            break;
        case T_TRUE:
            push(context, true);
            break;
        case T_FALSE:
            push(context, false);
            break;
        case T_STRING:
            // Save the string text without the quotes.
            push(context, token.text.slice(1, -1));
            break;
        default:
            unexpectedTokenError(context, token);
    }
}

// path: INPUT? pathReset
function path(context: InterpreterContext) {
    const token = peekToken(context);
    if (token.type === T_INPUT) {
        if (context.index === -1) {
            error(context, `Expecting "/", found "$" at ${token.index}`);
        }

        // Nothing to do, $ represents the items of interest that are already on the stack.
        nextToken(context);
    }

    pathRest(context);
}

// pathRest: PATH_SEPARATOR (wild | namedItem) path?
function pathRest(context: InterpreterContext) {
    let token = nextToken(context);
    expectToken(context, token.type, T_PATH_SEPARATOR);
    if (!stackTop(context)) {
        // This is the beginning, scene is the root node to start at.
        // Push a list, everything processes lists.
        //
        push(context, [context.scene]);
    }

    token = peekToken(context);
    switch (token.type) {
        case T_WILD:
        case T_WILD_RECURSIVE:
            wildCard(context);
            break;
        case T_PROPERTY_NAME:
        case T_CLASS_NAME:
            namedItem(context);
            break;
        case T_NUMBER:
            indexedItem(context);
            break;
        default:
            token = peekToken(context);
            error(context, 'Expecting a wild card or a class type or property name');
    }

    if (peekToken(context).type === T_PATH_SEPARATOR) {
        pathRest(context);
    }
}

// wild: WILD | WILD_RECURSIVE
function wildCard(context: InterpreterContext) {
    const token = nextToken(context);
    const nodes = pop(context) as BaseAsset[];

    switch (token.type) {
        case T_WILD:
            push(
                context,
                nodes.flatMap((n) => {
                    // handle nodes which contain assets with getAssets
                    if (n instanceof TimeAwareBase || n instanceof AssetGroup) {
                        return n.getAssets({ deep: false });
                    }
                    // otherwise just return the node
                    return n as unknown;
                }),
            );
            break;
        case T_WILD_RECURSIVE:
            push(
                context,
                nodes.flatMap((n) => n.getAssets()),
            );
            break;
        default:
            unexpectedTokenError(context, token);
    }

    pushPath(context, token);
}

// namedItem: PROPERTY_NAME | CLASS_NAME
function namedItem(context: InterpreterContext) {
    const token = nextToken(context);
    const nodes = stackTop(context) as (BaseAsset | NoMatch)[];

    switch (token.type) {
        case T_CLASS_NAME:
            push(
                context,
                (nodes as BaseAsset[]).filter((n) => n.classType === token.text),
            );
            break;
        case T_PROPERTY_NAME:
            push(
                context,
                nodes.map((n) => {
                    const property = token.text;
                    if (!(property in n)) {
                        if (n instanceof NoMatch) {
                            return n;
                        }
                        return new NoMatch(context, token, `Property '${property}' not found`);
                    }
                    return (n as unknown as Record<string, unknown>)[property];
                }),
            );
            break;
        default:
            unexpectedTokenError(context, token);
    }

    pushPath(context, token);
}

function indexedItem(context: InterpreterContext) {
    const token = nextToken(context);
    expectToken(context, T_NUMBER, token.type);
    const nodes = stackTop(context) as Record<string, unknown>[];

    if (nodes.length === 0) {
        return;
    }

    const index = Number(token.text);
    const nodeAtIndex = nodes[index];
    if (nodeAtIndex instanceof BaseAsset) {
        push(context, [nodes[index]]);
        pushPath(context, token);
        return;
    }

    const result = nodes.map((n) => {
        if (n instanceof NoMatch) {
            return n;
        }

        if (!(n instanceof Array)) {
            return new NoMatch(context, token, `Expecting an array type`);
        }

        if (index >= n.length) {
            return new NoMatch(context, token, `Index ${index} is out of bounds`);
        }

        return n[index];
    });
    push(context, result);
    pushPath(context, token);
}
